import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Icon, inkA, orange, tokens } from 'components/v3';
import {
  AGREE_LABEL_DEFAULT,
  OTHER_LABEL,
  fieldTypeLabel,
  typeLabel,
} from '../../shared/fieldTypes';
import { brand } from '../../shared/brand';
import { checkMapping, sourceLabel } from '../../shared/prefillSources';
import { embedUrl } from '../../shared/video';
import AssetSlot from './AssetSlot';

// The canvas's static picture of a field (design: canvas_fields.jsx). Only
// divs — never real inputs — so nothing can receive a dragged palette item
// and nothing inside competes with the card's select button. The image
// slots may carry an upload button (onPickAsset), and an agreement's text
// box sits above the button so long text can scroll (a click on it still
// calls onActivate, which selects the card).
//
// `tags`: 'side' puts the Type / Required / Pre-fills tags top-right on the
// label's row (desktop cards; FieldCard slides them left while its
// Duplicate / Delete buttons show); 'below' gives them their own row under
// the label (phones and narrow cards).

const ERROR_RED = '#ED4337';
// "Pre-fills · check" text: the design's amber on dark; a deeper amber on
// light cards, where 245,158,11 is too faint to read (WCAG AA).
const amberText = (theme) =>
  theme && theme.name === 'dark' ? 'rgb(245, 158, 11)' : 'rgb(180, 83, 9)';
const EMAIL_DOMAINS = [
  '@gmail.com',
  '@yahoo.com',
  '@outlook.com',
  '@hotmail.com',
  '@icloud.com',
  '@aol.com',
];
export const IMAGE_SIZES = {
  small: { width: 150, height: 78 },
  medium: { width: 250, height: 130 },
  large: { width: 380, height: 200 },
};
const ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };

const Box = styled.div`
  width: 100%;
  min-height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};
  gap: 10px;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const useColors = () => {
  const theme = useTheme();
  const t = tokens(theme);
  return {
    t,
    theme,
    box: { $border: t.border, $bg: inkA(theme, 0.05), $color: t.faint },
  };
};

const Fake = ({ children, icon }) => {
  const { box } = useColors();
  return (
    <Box {...box}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </span>
      {icon ? <Icon name={icon} size={16} color="currentColor" /> : null}
    </Box>
  );
};

Fake.propTypes = { children: PropTypes.node, icon: PropTypes.string };
Fake.defaultProps = { children: null, icon: null };

const Mark = ({ round }) => {
  const { theme } = useColors();
  return (
    <span
      aria-hidden="true"
      style={{
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: round ? '50%' : 4,
        border: '2px solid ' + inkA(theme, 0.25),
        boxSizing: 'border-box',
      }}
    />
  );
};

Mark.propTypes = { round: PropTypes.bool };
Mark.defaultProps = { round: false };

const OptionRow = ({ children, round }) => {
  const { t } = useColors();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '7px 0',
        fontSize: 14,
        color: t.sub,
      }}
    >
      <Mark round={round} />
      <span style={{ lineHeight: 1.45, minWidth: 0, overflowWrap: 'anywhere' }}>
        {children}
      </span>
    </div>
  );
};

OptionRow.propTypes = { children: PropTypes.node, round: PropTypes.bool };
OptionRow.defaultProps = { children: null, round: false };

const MiniTable = ({ head, rows, trailing }) => {
  const { t, theme } = useColors();
  const cell = (key, content, extra) => (
    <div
      key={key}
      style={{
        flex: 1,
        minWidth: 0,
        padding: '8px 10px',
        borderLeft: key.endsWith('-0') ? 'none' : '1px solid ' + t.border,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...extra,
      }}
    >
      {content}
    </div>
  );
  return (
    <div
      style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        fontSize: 13,
        color: t.sub,
      }}
    >
      <div
        style={{
          display: 'flex',
          background: inkA(theme, 0.04),
          color: t.text,
          fontWeight: 600,
        }}
      >
        {head.map((h, i) => cell(`h-${i}`, h))}
        {trailing ? cell('h-trail', '', { flex: 'none', width: 28 }) : null}
      </div>
      {rows.map((row, r) => (
        <div
          key={`r${r}`}
          style={{ display: 'flex', borderTop: '1px solid ' + t.border }}
        >
          {row.map((c, i) => cell(`r${r}-${i}`, c))}
          {trailing
            ? cell(`r${r}-trail`, trailing, {
                flex: 'none',
                width: 28,
                textAlign: 'center',
              })
            : null}
        </div>
      ))}
    </div>
  );
};

MiniTable.propTypes = {
  head: PropTypes.arrayOf(PropTypes.node).isRequired,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.node)).isRequired,
  trailing: PropTypes.node,
};

MiniTable.defaultProps = { trailing: null };

const list = (field, key) =>
  Array.isArray(field.config[key]) ? field.config[key] : [];

const NameBody = ({ field }) => (
  <Grid $columns={field.config.middle ? '1fr 1fr 1fr' : '1fr 1fr'}>
    <Fake>First</Fake>
    {field.config.middle ? <Fake>Middle</Fake> : null}
    <Fake>Last</Fake>
  </Grid>
);

const AddressBody = () => (
  <div style={{ display: 'grid', gap: 10 }}>
    <Fake>Street address</Fake>
    <Fake>Apt, suite, unit (optional)</Fake>
    <Grid $columns="2fr 1fr 1fr">
      <Fake>City</Fake>
      <Fake>State</Fake>
      <Fake>ZIP</Fake>
    </Grid>
  </div>
);

const EmailBody = ({ field }) => {
  const { t, theme } = useColors();
  return (
    <div>
      <Fake>{field.placeholder || 'name@example.com'}</Fake>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {EMAIL_DOMAINS.map((d) => (
          <span
            key={d}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: t.faint,
              padding: '4px 10px',
              borderRadius: 14,
              border: '1px solid ' + t.border,
              background: inkA(theme, 0.04),
            }}
          >
            {d}
          </span>
        ))}
      </div>
    </div>
  );
};

const PhoneBody = () => (
  <div style={{ display: 'flex', gap: 10 }}>
    <div style={{ width: 76, flex: 'none' }}>
      <Fake icon="chevron-down">+1</Fake>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Fake>(555) 000-0000</Fake>
    </div>
  </div>
);

export const OTHER_HINT = `Includes “${OTHER_LABEL}” with a box to type in`;

const ChoiceBody = ({ field, round }) => (
  <div>
    {list(field, 'options').map((o, i) => (
      <OptionRow key={i} round={round}>
        {o}
      </OptionRow>
    ))}
    {field.config.allow_other === true ? (
      <OptionRow key="other" round={round}>
        {OTHER_LABEL}…
      </OptionRow>
    ) : null}
  </div>
);

const DropdownBody = ({ field }) => {
  const { t } = useColors();
  return (
    <div>
      <Fake icon="chevron-down">{field.placeholder || 'Select…'}</Fake>
      {field.config.allow_other === true ? (
        <div style={{ marginTop: 6, fontSize: 12.5, color: t.faint }}>
          {OTHER_HINT}
        </div>
      ) : null}
    </div>
  );
};

DropdownBody.propTypes = { field: PropTypes.object.isRequired };

// An agreement (design .fp-consent): the text keeps its line breaks and
// scrolls in its own 176px box, with the checkbox text under it.
export const agreeLabelOf = (field) => {
  const text = field && field.config ? field.config.agree_label : '';
  return typeof text === 'string' && text.trim()
    ? text.trim()
    : AGREE_LABEL_DEFAULT;
};

const ConsentBody = ({ field, onActivate }) => {
  const { t, theme } = useColors();
  return (
    <div
      style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        data-fb-agreement-text=""
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
          if (onActivate) onActivate();
        }}
        style={{
          position: 'relative',
          zIndex: 2,
          maxHeight: 176,
          overflowY: 'auto',
          padding: '12px 14px',
          fontSize: 14,
          lineHeight: 1.55,
          color: t.sub,
          background: inkA(theme, 0.04),
          whiteSpace: 'pre-line',
          overflowWrap: 'anywhere',
        }}
      >
        {list(field, 'options')[0] || ''}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 14px',
          borderTop: '1px solid ' + t.border,
          fontSize: 14,
          fontWeight: 600,
          color: t.text,
          overflowWrap: 'anywhere',
        }}
      >
        <Mark />
        <span style={{ minWidth: 0 }}>{agreeLabelOf(field)}</span>
      </div>
    </div>
  );
};

const FileBody = () => {
  const { t, theme } = useColors();
  return (
    <div
      style={{
        border: '1.5px dashed ' + inkA(theme, 0.22),
        borderRadius: 8,
        padding: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: t.faint,
        fontSize: 14,
      }}
    >
      <Icon name="upload" size={18} color="currentColor" />
      Tap to upload or drag a file here
    </div>
  );
};

const RatingBody = ({ field }) => {
  const { theme } = useColors();
  const max = Number(field.config.max) || 5;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {Array.from({ length: max }, (_, i) => (
        <Icon key={i} name="star" size={24} color={inkA(theme, 0.3)} />
      ))}
    </div>
  );
};

const TableBody = ({ field }) => {
  const columns = list(field, 'columns');
  const rows = Math.max(1, Number(field.config.rows) || 1);
  return (
    <MiniTable
      head={columns}
      rows={Array.from({ length: rows }, () => columns.map(() => ''))}
    />
  );
};

const RepeaterBody = ({ field }) => {
  const { t } = useColors();
  const columns = list(field, 'columns');
  return (
    <div>
      <MiniTable
        head={columns}
        rows={[columns.map(() => '')]}
        trailing={<Icon name="x" size={12} color="currentColor" />}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 8,
          fontSize: 13,
          fontWeight: 600,
          color: orange(1),
        }}
      >
        <Icon name="plus" size={14} color="currentColor" />
        {field.config.add_label || 'Add another'}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            fontWeight: 500,
            color: t.faint,
          }}
        >
          {`up to ${field.config.max}`}
        </span>
      </div>
    </div>
  );
};

const MatrixBody = ({ field }) => {
  const rows = list(field, 'rows');
  const cols = list(field, 'cols');
  return (
    <MiniTable
      head={['', ...cols]}
      rows={rows.map((r) => [r, ...cols.map((c) => <Mark key={c} round />)])}
    />
  );
};

const BODIES = {
  short_answer: ({ field }) => <Fake>{field.placeholder}</Fake>,
  long_answer: ({ field }) => (
    <div style={{ minHeight: 76, display: 'flex' }}>
      <Fake>{field.placeholder}</Fake>
    </div>
  ),
  name: NameBody,
  address: AddressBody,
  email: EmailBody,
  phone: PhoneBody,
  number: ({ field }) => <Fake>{field.placeholder || '0'}</Fake>,
  dropdown: DropdownBody,
  radio: ({ field }) => <ChoiceBody field={field} round />,
  checkbox: ({ field, onActivate }) =>
    field.config.single ? (
      <ConsentBody field={field} onActivate={onActivate} />
    ) : (
      <ChoiceBody field={field} />
    ),
  datetime: ({ field }) => (
    <Fake icon="calendar">{field.placeholder || 'MM / DD / YYYY'}</Fake>
  ),
  file_upload: FileBody,
  rating: RatingBody,
  table: TableBody,
  repeater: RepeaterBody,
  matrix: MatrixBody,
};

const TypeTag = ({ children, tone }) => {
  const { t, theme } = useColors();
  const tones = {
    plain: {
      color: t.faint,
      background: inkA(theme, 0.06),
      border: 'transparent',
    },
    ok: { color: orange(1), background: orange(0.1), border: orange(0.35) },
    warn: {
      color: amberText(theme),
      background: 'rgba(245, 158, 11, 0.1)',
      border: 'rgba(245, 158, 11, 0.4)',
    },
    blocked: {
      color: ERROR_RED,
      background: 'rgba(237, 67, 55, 0.1)',
      border: 'rgba(237, 67, 55, 0.4)',
    },
  };
  const s = tones[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        lineHeight: 1.2,
        padding: '4px 7px',
        borderRadius: 5,
        color: s.color,
        background: s.background,
        border: '1px solid ' + s.border,
      }}
    >
      {children}
    </span>
  );
};

TypeTag.propTypes = {
  children: PropTypes.node.isRequired,
  tone: PropTypes.string,
};
TypeTag.defaultProps = { tone: 'plain' };

const MAP_SUFFIX = { ok: '', warn: ' · check', blocked: ' · blocked' };

const Label = ({ children, required, reserve, mark, side }) => {
  const { t } = useColors();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.35,
        color: t.text,
        marginBottom: side ? 0 : 4,
        paddingRight: reserve,
        overflowWrap: 'anywhere',
        ...(side ? { flex: 1, minWidth: 0 } : {}),
      }}
    >
      <span>{children}</span>
      {required ? (
        <span
          aria-hidden="true"
          style={{
            color: ERROR_RED,
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
            alignSelf: 'flex-start',
          }}
        >
          *
        </span>
      ) : null}
      {mark}
    </div>
  );
};

Label.propTypes = {
  children: PropTypes.node,
  required: PropTypes.bool,
  reserve: PropTypes.number,
  mark: PropTypes.node,
  side: PropTypes.bool,
};

Label.defaultProps = {
  children: null,
  required: false,
  reserve: 0,
  mark: null,
  side: false,
};

// The tag row. FieldCard's CSS slides `.fb-field-tags` left on hover.
const Meta = ({ children, reserve, side }) => (
  <div
    className="fb-field-tags"
    data-tour="field-tags"
    style={
      side
        ? {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            flex: '0 1 auto',
            maxWidth: '50%',
            minWidth: 0,
          }
        : {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            margin: '0 0 10px',
            paddingRight: reserve,
          }
    }
  >
    {children}
  </div>
);

Meta.propTypes = {
  children: PropTypes.node,
  reserve: PropTypes.number,
  side: PropTypes.bool,
};
Meta.defaultProps = { children: null, reserve: 0, side: false };

// Label + tags, laid out for `tags` ('side' or 'below').
const Head = ({ tags, reserve, label, required, mark, children }) => {
  const side = tags === 'side';
  const labelRow = (
    <Label required={required} reserve={reserve} mark={mark} side={side}>
      {label}
    </Label>
  );
  if (!side) {
    return (
      <>
        {labelRow}
        <Meta reserve={reserve}>{children}</Meta>
      </>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 10,
      }}
    >
      {labelRow}
      <Meta side>{children}</Meta>
    </div>
  );
};

Head.propTypes = {
  tags: PropTypes.oneOf(['side', 'below']).isRequired,
  reserve: PropTypes.number.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  mark: PropTypes.node,
  children: PropTypes.node,
};

Head.defaultProps = {
  label: null,
  required: false,
  mark: null,
  children: null,
};

const Help = ({ children }) => {
  const { t } = useColors();
  return children ? (
    <div
      style={{
        fontSize: 13,
        color: t.faint,
        marginTop: 6,
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </div>
  ) : null;
};

Help.propTypes = { children: PropTypes.node };
Help.defaultProps = { children: null };

const SectionPreview = ({ field, assetUrl, onPickAsset, uploading }) => {
  const { t } = useColors();
  return (
    <div style={{ paddingTop: 4 }}>
      {field.config.banner ? (
        <div style={{ marginBottom: 12 }}>
          <AssetSlot
            url={assetUrl(field.config.banner_asset_id)}
            width="100%"
            height={128}
            fit="cover"
            placeholder="Add a header image (wide, landscape)"
            alt={`${field.label} header`}
            busy={uploading}
            onPick={
              onPickAsset
                ? (file) => onPickAsset('banner_asset_id', file)
                : undefined
            }
          />
        </div>
      ) : null}
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: t.text,
          overflowWrap: 'anywhere',
        }}
      >
        {field.label || 'Untitled section'}
      </div>
      {field.help ? (
        <div style={{ fontSize: 13, color: t.faint, marginTop: 3 }}>
          {field.help}
        </div>
      ) : null}
    </div>
  );
};

const ImagePreview = ({
  field,
  assetUrl,
  onPickAsset,
  uploading,
  reserve,
  tags,
}) => {
  const size = IMAGE_SIZES[field.config.size] || IMAGE_SIZES.medium;
  return (
    <div>
      <Head tags={tags} reserve={reserve} label={field.label || 'Logo / Image'}>
        <TypeTag>{typeLabel(field.type)}</TypeTag>
      </Head>
      <div
        style={{
          display: 'flex',
          justifyContent: ALIGN[field.config.align] || 'center',
        }}
      >
        <AssetSlot
          url={assetUrl(field.config.asset_id)}
          width={size.width}
          height={size.height}
          placeholder="Add a logo or image"
          alt={field.label || 'Logo'}
          busy={uploading}
          onPick={
            onPickAsset ? (file) => onPickAsset('asset_id', file) : undefined
          }
        />
      </div>
    </div>
  );
};

const VideoEmbed = ({ url, title }) => (
  <div
    style={{
      position: 'relative',
      aspectRatio: '16 / 9',
      maxWidth: 480,
      borderRadius: 10,
      overflow: 'hidden',
      background: 'rgb(0, 0, 0)',
    }}
  >
    <iframe
      src={url}
      title={title}
      tabIndex={-1}
      allow="fullscreen; picture-in-picture"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0,
        pointerEvents: 'none',
      }}
    />
  </div>
);

VideoEmbed.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const NoVideo = () => {
  const { t, theme } = useColors();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        aspectRatio: '16 / 9',
        maxWidth: 480,
        padding: 12,
        boxSizing: 'border-box',
        borderRadius: 10,
        border: '1.5px dashed ' + inkA(theme, 0.22),
        textAlign: 'center',
        fontSize: 13,
        color: t.faint,
      }}
    >
      <Icon name="circle-play" size={34} color="currentColor" />
      Paste a YouTube or Vimeo link in the settings panel
    </div>
  );
};

const VideoPreview = ({ field, reserve, tags }) => {
  const src = embedUrl(field.config.url);
  return (
    <div>
      <Head tags={tags} reserve={reserve} label={field.label || 'Video'}>
        <TypeTag>{typeLabel(field.type)}</TypeTag>
      </Head>
      {src ? (
        <VideoEmbed url={src} title={field.label || 'Video'} />
      ) : (
        <NoVideo />
      )}
      <Help>{field.config.caption}</Help>
    </div>
  );
};

const DescriptionPreview = ({ field, reserve, tags }) => {
  const { t } = useColors();
  return (
    <div>
      <Head tags={tags} reserve={reserve} label={field.label}>
        <TypeTag>{typeLabel(field.type)}</TypeTag>
      </Head>
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: t.sub,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {field.config.text}
      </div>
    </div>
  );
};

const LAYOUT_PREVIEWS = {
  section: SectionPreview,
  image: ImagePreview,
  video: VideoPreview,
  description: DescriptionPreview,
};

// The tooltip (help/tooltips.js "prefill-tag") carries the mapping check's
// message, so a caveat reads as a caveat.
const MapTag = ({ field }) => {
  const check = checkMapping(field) || { level: 'ok', message: '' };
  return (
    <span
      title={check.message}
      data-help="prefill-tag"
      data-tip-title={
        check.level === 'ok' ? `${brand().name} field` : 'Check the pre-fill'
      }
      data-tip={check.message || undefined}
      style={{ display: 'inline-flex', maxWidth: '100%' }}
    >
      <TypeTag tone={check.level}>
        <Icon
          name={check.level === 'ok' ? 'user' : 'triangle-alert'}
          size={11}
          color="currentColor"
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {`Pre-fills: ${sourceLabel(field.map)}${MAP_SUFFIX[check.level]}`}
        </span>
      </TypeTag>
    </span>
  );
};

MapTag.propTypes = { field: PropTypes.object.isRequired };

const FieldPreview = ({
  field,
  assetUrl,
  onPickAsset,
  onActivate,
  uploading,
  reserve,
  labelMark,
  tags,
}) => {
  const Layout = LAYOUT_PREVIEWS[field.type];
  if (Layout) {
    return (
      <Layout
        field={field}
        assetUrl={assetUrl}
        onPickAsset={onPickAsset}
        uploading={uploading}
        reserve={reserve}
        tags={tags}
      />
    );
  }
  const Body = BODIES[field.type];
  return (
    <div>
      <Head
        tags={tags}
        reserve={reserve}
        label={field.label}
        required={field.required}
        mark={field.map ? labelMark : null}
      >
        <TypeTag>{fieldTypeLabel(field)}</TypeTag>
        {field.required ? <TypeTag>Required</TypeTag> : null}
        {field.map ? <MapTag field={field} /> : null}
      </Head>
      {Body ? <Body field={field} onActivate={onActivate} /> : null}
      <Help>{field.help}</Help>
    </div>
  );
};

FieldPreview.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    help: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    map: PropTypes.string,
    config: PropTypes.object.isRequired,
  }).isRequired,
  assetUrl: PropTypes.func,
  onPickAsset: PropTypes.func,
  onActivate: PropTypes.func,
  uploading: PropTypes.bool,
  reserve: PropTypes.number,
  labelMark: PropTypes.node,
  tags: PropTypes.oneOf(['side', 'below']),
};

FieldPreview.defaultProps = {
  assetUrl: () => null,
  onPickAsset: undefined,
  onActivate: undefined,
  uploading: false,
  reserve: 0,
  labelMark: null,
  tags: 'below',
};

export default FieldPreview;
